import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import { CheckClimaEl } from '../../Co2Climate/style'
import { Button, Message, Progress } from 'semantic-ui-react'
import { getStepHeadMessage, preparePayload } from './util'
import { getDairySteps, HERD_GROUP_ERROR_CLASS, MILK_COWS_SLUG, StepId } from './constants'
import { FormProvider, useForm } from 'react-hook-form'
import Milk from './Steps/Milk'
import Energy from './Steps/Energy'
import Feed from './Steps/Feed'
import Grazing from './Steps/Grazing'
import Herd from './Steps/Herd'
import Manure from './Steps/Manure'
import Transport from './Steps/Transport'
import { CoolFarmEl } from './style'
import Step from './Step'
import { CoolFarmOptionsType } from './types'
import { Nullable } from '~/types'
import { handleErrors } from '~/utils/helpers'
import { CoolFarmDairyResultsType, CoolFarmService } from '~/api/services/coolFarm'
import { useNavigate, useParams } from 'react-router-dom'
import notify from '~/components/Notify'
import { AnsweredContainer, LogoWrapper, SubmitHeader } from '../../Co2Climate/AnswerCO2Climate/style'
import Results from './Steps/Results'
import cloneDeep from 'lodash.clonedeep'
import WelcomeModal from './WelcomeModal'
import dayjs from 'dayjs'
import Dialog from '~/components/Dialog'
import { ThemeContext } from 'styled-components'

export const CoolFarmOptionsContext = createContext<CoolFarmOptionsType>(null)

const LAST_STEP_INDEX = 7

const AnswerCoolFarm: React.FC = () => {
  const { t } = useTranslation()
  const stepsRef = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  const navigate = useNavigate()
  const { logoTitle } = useContext(ThemeContext)

  const [activeTab, setActiveTab] = useState(0)
  const [isBusy, setIsBusy] = useState(false)
  const [steps, setSteps] = useState(getDairySteps())
  const [progress, setProgress] = useState(0)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(true)
  const [options, setOptions] = useState<Nullable<CoolFarmOptionsType>>(null)
  const [results, setResults] = useState<Nullable<CoolFarmDairyResultsType>>(null)
  const [manureWarningModalOpen, setManureModalWarningOpen] = useState(false)

  const methods = useForm({
    'mode': 'onSubmit'
  })

  useEffect(() => {
    checkStatus()
    setSteps(getDairySteps())
  }, [t])

  useEffect(() => {
    if (stepsRef.current && activeTab <= 8) {
      const container = stepsRef.current
      const containerRect = container.getBoundingClientRect()
      const children = Array.from(container.children)
      const targetEl = children[activeTab] as HTMLElement | undefined
      
      if (targetEl) {
        const targetRect = targetEl.getBoundingClientRect()
        
        const containerCenter = containerRect.left + containerRect.width / 2
        const stepCenter = targetRect.left + targetRect.width / 2
        
        const distanceFromCenter = stepCenter - containerCenter
        const maxDistance = containerRect.width / 2
        
        const progressPercent = Math.max(0, Math.min(100, 50 + (distanceFromCenter / maxDistance) * 50))
        setProgress(progressPercent)
      }
    }
  }, [activeTab, stepsRef])

  function handleExpired() {
    notify(t('Cool Farm slot expired'), 'error')
    navigate(RouteTypes.CO2_CLIMATE)
  }

  async function handleCoolFarmInitial() {
    try {
      const response = await CoolFarmService.getCoolFarmDairyAnsOptions(id)
      const milkCow = response.data?.herdCategories?.find(herCategory => herCategory.slug === MILK_COWS_SLUG)
      setOptions(response.data)
      methods.reset({
        herdGroups: [{
          herdCategoryId: milkCow.id,
          animalsNumber: '',
          animalsLiveWeight: '',
          soldAnimalsNumber: '',
          soldAnimalsLiveWeight: '',
          purchasedAnimalsNumber: '',
          purchasedAnimalsLiveWeight: ''
        }]
      })
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleEdit() {
    try {
      const response = await CoolFarmService.getCoolFarmDairyAnsOptions(id)
      const responseData = await CoolFarmService.editCoolFarmAnswers(id)
      if(responseData?.data?.fertilizers?.length > 0) {
        responseData.data.fertilizers.forEach(fertilizer => {
          fertilizer.applicationDate = dayjs(fertilizer.applicationDate).format('DD.MM.YYYY')
        })
      }
      methods.reset({
        ...responseData.data
      })
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleDraft() {
    try {
      const response = await CoolFarmService.getCoolFarmDairyAnsOptions(id)
      const responseData = await CoolFarmService.editCoolFarmAnswers(id)
      if(responseData?.data?.fertilizers?.length > 0) {
        responseData.data.fertilizers.forEach(fertilizer => {
          fertilizer.applicationDate = dayjs(fertilizer.applicationDate).format('DD.MM.YYYY')
        })
      }
      methods.reset(responseData.data)
      setOptions(response.data)
      setActiveTab(responseData?.data?.step || 0)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function checkStatus() {
    setIsBusy(true)
    try {
      /* answerStatus = 0 - initial answer (no draft, no answer yet, co2 slot active)
      answerStatus = 1 -  edit
      answerStatus = 2 - draft
      answerStatus = 3 - not available anymore (expired) */
      const responseStatus = await CoolFarmService.checkCoolFarmAnswers(id, { farmerId: null })
      switch (responseStatus.data.answerStatus) {
        //initial answer (no draft, no answer yet, co2 slot active)
        case 0:
          return handleCoolFarmInitial()
        //edit
        case 1:
          return handleEdit()
        //draft
        case 2:
          return handleDraft()
        //not available anymore (expired)
        case 3:
          return handleExpired()
      }
    } catch (error) {
      handleErrors(error)
      navigate(RouteTypes.COOL_FARM)
    }
    setIsBusy(false)
  }

  function renderSteps() {
    return <CoolFarmEl.Steps ref={stepsRef}>
      {steps.map((step, index) => {
        return <Step
          step={step}
          index={index}
          key={step.title}
          handleStepClick={() => setActiveTab(index)}
          active={activeTab === index}
          isLoading={activeTab === index && isLoadingResults}
          isLoadingResults={isLoadingResults}
          lastStepIndex={8}
        />
      })}
    </CoolFarmEl.Steps>
  }

  function renderActiveComponentStep() {
    const id = steps[activeTab].id
    switch (id) {
      case StepId.MILK:
        return <Milk />
      case StepId.ENERGY_PROCESSING:
        return <Energy />
      case StepId.FEED:
        return <Feed />
      case StepId.GRAZING:
        return <Grazing />
      case StepId.HERD:
        return <Herd />
      case StepId.MANURE:
        return <Manure />
      case StepId.TRANSPORT:
        return <Transport />
      case StepId.RESULTS:
        return <Results results={results}/>
    }
  }

  function renderPreviousButton() {

    return <Button
      type='button'
      onClick={() => setActiveTab(activeTab - 1)}
      content={t('Previous')}
    />
  }

  function renderNextButton() {

    return <Button
      type='submit'
      content={t('Continue')}
    />
  }

  async function handleLoadResults() {
    setIsLoadingResults(true)
    try {
      const data = cloneDeep(methods.getValues())
      const payload = preparePayload(data)
      await CoolFarmService.saveCoolFarmAnswers(id, { ...payload })
      const response = await CoolFarmService.getResultsCoolFarm(id, payload)
      setResults(response.data)
      setActiveTab(activeTab + 1)
    } catch(error) {
      handleErrors(error)
    } finally {
      setIsLoadingResults(false)
    }
  }

  async function handleSaveDraft(data) {
    setIsLoadingResults(true)
    try {
      const payload = preparePayload(data)
      await CoolFarmService.saveCoolFarmAnswers(id, { ...payload, step: activeTab + 1 })
      setActiveTab(activeTab + 1)
    }catch(error) {
      handleErrors(error)
    }finally {
      setIsLoadingResults(false)
    }
  }

  function handleValidationFeeds(data) {
    let hasErrors = false
    data.herdGroups?.map(herdGroup => {
      if(herdGroup?.feeds?.length === 0) {
        const herdGroupElement = document.getElementById(herdGroup.id)
        if(herdGroupElement) {
          herdGroupElement.classList.add(HERD_GROUP_ERROR_CLASS)
        }
        hasErrors = true
      }
    })
    if(hasErrors) {
      notify(t('Please add feeds for all herd groups'), 'error')
      return
    }
    handleSaveDraft(data)
  }

  function handleValidateManureStep(data) {
    let hasErrors = false
    const invalidPercentageGroups = []

    data.herdGroups?.map(herdGroup => {
      if(herdGroup?.manureStorageTypes?.length === 0 || !herdGroup?.manureStorageTypes) {
        const herdGroupElement = document.getElementById(`manure-${herdGroup.id}`)
        if(herdGroupElement) {
          herdGroupElement.classList.add(HERD_GROUP_ERROR_CLASS)
        }
        hasErrors = true
      } else {
        const totalPercent = herdGroup.manureStorageTypes.reduce((sum, manure) => {
          const percent = parseFloat(manure?.percent) || 0
          return sum + percent
        }, 0)

        if(totalPercent !== 100) {
          const herdGroupElement = document.getElementById(`manure-${herdGroup.id}`)
          if(herdGroupElement) {
            herdGroupElement.classList.add(HERD_GROUP_ERROR_CLASS)
          }
          invalidPercentageGroups.push(herdGroup)
          hasErrors = true
        }
      }
    })

    if(hasErrors) {
      if(invalidPercentageGroups.length > 0) {
        notify(t('Manure storage percentages must sum to 100% for each herd group'), 'error')
      } else {
        setManureModalWarningOpen(true)
      }
      return
    }
    handleSaveDraft(data)
  }

  async function onSubmit(data) {
    const isFeedStep = steps[activeTab].id === StepId.FEED
    const isManureStep = steps[activeTab].id === StepId.MANURE
    if(activeTab + 1 === LAST_STEP_INDEX) {
      handleLoadResults()
      return
    }

    if(isFeedStep) {
      handleValidationFeeds(data)
      return
    }

    if(isManureStep) {
      handleValidateManureStep(data)
      return
    }
    
    handleSaveDraft(data)
  }

  async function handleSubmitAnswers() {
    const data = cloneDeep(methods.getValues())
    const payload = preparePayload(data)
    setIsBusy(true)
    try {
      await CoolFarmService.storeCoolFarmAnswers(id, { ...payload, step: activeTab })
      setIsSubmitModalOpen(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderAnwseredPercent() {
    return <div style={{ display: 'flex', alignItems: 'anchor-center', gap: 20 }}>
      <AnsweredContainer>
        <Progress value={activeTab + 1} total={steps.length} progress={'ratio'} />
      </AnsweredContainer>
    </div>
  }

  function renderSubmitButton() {
    return <Button
      style={{ minWidth: 225, color: 'white' }}
      type={'button'}
      disabled={Object.keys(methods.formState.errors).length > 0 || isLoadingResults}
      content={t('Complete data collection')}
      onClick={() => handleSubmitAnswers()}
    />
  }

  function renderGeneralInformation() {
    return <WelcomeModal
      isOpen={isWelcomeOpen}
      handleClose={() => setIsWelcomeOpen(false)}
    />
  }

  function renderSubmitModal() {
    return <Dialog
      textHeader={''}
      isDialogOpen={isSubmitModalOpen}
      textSubmit={t('Back to home page')}
      hideCloseIcon
      handleSubmit={() => {
        setIsSubmitModalOpen(false)
        navigate(RouteTypes.COOL_FARM)
      }
      }
      textContent={<div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>
          <SubmitHeader>{t('Thank you for submitting your data.')}</SubmitHeader>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div>{t('You have the option to edit the data during the survey period.')}</div>
          <div>{t('After the end of the survey period, you will receive a detailed PDF document with all analyses and input values.')}</div>
        </div>
        <LogoWrapper style={{ backgroundImage: `url(${logoTitle})` }} />
      </div>}
    />
  }

  function handleSubmitManure() {
    const data = cloneDeep(methods.getValues())
    setManureModalWarningOpen(false)
    handleSaveDraft(data)
  }

  function renderManureWarningModal() {
    const herdGroups = methods.getValues('herdGroups') || []
    const herdGroupsWithManureSelected = herdGroups.filter(herdGroup => herdGroup?.manureStorageTypes?.length === 0 || !herdGroup?.manureStorageTypes)
    const herdGroupLabels = options?.herdCategories?.map(herdCategory => {
      if(herdGroupsWithManureSelected.find(herdGroup => herdCategory.id === herdGroup.herdCategoryId)) {
        return herdCategory.label
      }
      return
    }) || []

    return <Dialog
      isDialogOpen={manureWarningModalOpen}
      handleClose={() => setManureModalWarningOpen(false)}
      textClose={t('Cancel')}
      textHeader={t('Manure')}
      textSubmit={t('Continue')}
      type='abort'
      textContent={<div>
        <div dangerouslySetInnerHTML={{ __html: t('Herd Groups without manure selected: {{herdLabels}}', { herdLabels: herdGroupLabels.filter(herdGroup => herdGroup)?.join(' ,') }) }}/>
        <div>{t('If no manure management is selected for an animal category, grazing will be assumed.')}</div>
      </div>}
      handleSubmit={() => handleSubmitManure()}
    />
  }

  return <Layout
    title={t('Cool Farm survey')}
    segmented
    isBusy={isBusy}
    isCo2Loader
    close={RouteTypes.COOL_FARM}
    actions={renderAnwseredPercent()}
  >
    {renderSubmitModal()}
    {renderGeneralInformation()}
    <CoolFarmEl.StepsContainer>
      <Progress
        percent={progress}
        size='tiny'
        active
        style={{
          position: 'absolute',
          width: '100%',
          top: 23
        }}
      />
      {renderManureWarningModal()}
      {renderSteps()}
      {<Message info style={{
        borderRadius: '10px',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'fit-content',
        textAlign: 'center',
        marginTop: '2.5em'
      }}>
        <Message.Content>
          {<div dangerouslySetInnerHTML={{ __html: getStepHeadMessage(steps[activeTab].id as StepId) }} />}
        </Message.Content>
      </Message>}
    </CoolFarmEl.StepsContainer>
    <CheckClimaEl.WizzardContentWrapper onSubmit={methods.handleSubmit(onSubmit)}>
      <CheckClimaEl.StyledContent $isFirstStep={activeTab === 0}>
        <CoolFarmOptionsContext.Provider value={options as CoolFarmOptionsType}>
          <FormProvider {...methods}>
            {options && renderActiveComponentStep()}
          </FormProvider>
        </CoolFarmOptionsContext.Provider>
      </CheckClimaEl.StyledContent>
      <CheckClimaEl.ActionsContainer $activeTab={activeTab}>
        {activeTab > 0 && renderPreviousButton()}
        {activeTab < LAST_STEP_INDEX && renderNextButton()}
        {activeTab === LAST_STEP_INDEX && renderSubmitButton()}
      </CheckClimaEl.ActionsContainer>
    </CheckClimaEl.WizzardContentWrapper>
  </Layout>
}
export default AnswerCoolFarm